/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, RefObject } from 'preact';
import type { BundleType } from '#resources/nls/bundle';
import type { TableNoDataRendererContext } from '#UNSAFE_TableView';
import type { TableNoDataVariantOptions } from './themes/TableNoDataStyles.css';

import { useLayoutEffect } from 'preact/hooks';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { tableRowStyles } from './themes/TableRowStyles.css';
import { TableNoDataRedwoodTheme } from './themes/redwood/TableNoDataTheme';
import { getNoDataCellElement, getTableBodyElement } from './utils/TableDomUtils';

/**
 * Props for the TableNoData Component
 */
type TableNoDataProps = {
  rootRef: RefObject<HTMLDivElement>;
  tableId: string;
  columnsCount: number;
  isShowFocusRing: boolean;
  hasTabIndex: boolean;
  isTabbableMode: boolean;
  ariaRowIndex: number;
  renderer?: (context: TableNoDataRendererContext) => ComponentChildren;
};

/**
 * The internal component used to render an empty table cell in a TableBody.
 */
export function TableNoData({
  rootRef,
  tableId,
  columnsCount,
  isShowFocusRing,
  hasTabIndex,
  isTabbableMode,
  ariaRowIndex,
  renderer
}: TableNoDataProps) {
  const browser = getClientHints().browser;
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const rowClasses = classNames([tableRowStyles.base]);

  const { classes: themeClasses } = useComponentTheme<TableNoDataVariantOptions>(
    TableNoDataRedwoodTheme,
    {
      defaultRenderer: renderer == null ? 'isDefaultRenderer' : 'notDefaultRenderer',
      showFocusRing: isShowFocusRing ? 'isShowFocusRing' : 'notShowFocusRing'
    }
  );

  const cellClasses = classNames([themeClasses]);

  function defaultContentRenderer() {
    return translations.collection_noData();
  }

  const contentRenderer = renderer != null ? renderer : defaultContentRenderer;

  // in firefox, table element heights are not stretched unless explicitly set in some cases
  useLayoutEffect(() => {
    if (browser === 'firefox') {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        const tableBodyElement = getTableBodyElement(rootElement, tableId);
        const noDataElement = getNoDataCellElement(rootElement, tableId);
        if (tableBodyElement != null && noDataElement != null) {
          noDataElement.style.height = '';
          noDataElement.style.height = `${tableBodyElement.getBoundingClientRect().height}px`;
        }
      }
    }
  });

  return (
    <tr aria-rowindex={ariaRowIndex} class={rowClasses} role={'row'}>
      <td
        aria-colindex={1}
        class={cellClasses}
        style={browser !== 'firefox' ? 'height: 100%;' : undefined}
        tabIndex={hasTabIndex ? 0 : -1}
        role={'gridcell'}
        colSpan={columnsCount}
        data-oj-cell-type={'noData'}
        data-oj-table-focusable={tableId}
        data-oj-table-nodata={tableId}>
        {contentRenderer({ isTabbable: isTabbableMode })}
      </td>
    </tr>
  );
}
